/**
 * The storyboard's clock, derived from its durations.
 *
 * Nothing stores a start time as the truth. The agent publishes how long each
 * shot runs in the edit (`cutToMs`) and, for a line with no shots, how long
 * the line runs (its voiceover's length); everything else - where each shot
 * sits in the ad, where each line starts, how long the whole thing is - is
 * computed here, by the API after any structural or duration change and by
 * the web while somebody is still typing. One function in one place, so the
 * number on the server and the number on the screen cannot disagree.
 *
 * The rules, from the contract (`02-storyboard-v2-contract.md`, section 6):
 *  - shots in a line run back to back, in position order, by `cutToMs`;
 *  - a line with shots lasts as long as its shots; a line without lasts its
 *    explicit `durationMs` (below), and is unknown without one;
 *  - ordinary lines run back to back, in position order;
 *  - hook variants (kind `hook`) start at 0 - they replace the opening - and
 *    add nothing to the runtime;
 *  - the runtime is the sum of the ordinary lines.
 *
 * A shot whose `cutToMs` is null keeps a null `cutToMs` in the result and
 * counts as `UNKNOWN_CUT_MS` (2 s) in every sum - the one rule every reader
 * uses (the script page, the line page, the deck, the download, the clip
 * editor and its rough cut; clip-editor D1) - so its line's duration, every
 * later start and the runtime are estimates, and the line and the whole
 * timeline say so with `partial: true`. A shot-less line with no length of
 * its own counts as 0 and is partial too. A reader that needs the real
 * number asks for the missing cut.
 *
 * A stored line has one `durationMs` column for two things: its own length
 * (set by a person or the agent while it had no shots - its voiceover's
 * length, say) and the sum its shots made, which the API writes back so a v1
 * reader sees a number. `meta.durationSet: true` marks the first. Read back
 * unmarked, the stored sum would time a line that has just lost its last
 * shot by the shots it no longer has, and count their time twice; so a line
 * that carries a `meta` is timed by its `durationMs` only when the mark says
 * it is its own (`explicitLineDurationMs`), and `storedLineDurationMs` says
 * what the row holds once the clock has run: its own length, kept through
 * shots coming and going, or else the sum when every cut is known, and null
 * when it is not.
 *
 * Pure and deterministic: no clock, no I/O, no mutation of the input, the same
 * answer for the same rows whatever order they arrive in. Archived rows are
 * the caller's to leave out; this module times what it is given.
 */

/** How long a shot whose cut is still unknown is counted, drawn and rendered (flagged `partial`). */
export const UNKNOWN_CUT_MS = 2_000;

/** The key in a stored line's `meta` that marks its `durationMs` as the line's own length rather than its shots' sum. */
export const LINE_DURATION_SET = "durationSet";

/** A shot, as much of it as the clock needs. Satisfied by `StoryboardShot`. */
export interface TimelineShotSource {
  key: string;
  position: number;
  /** How long it runs in the edit, in milliseconds; null when not known yet. */
  cutToMs: number | null;
  /**
   * `overlay`: a Video 2 shot (a B-roll over the cut, migration 0045). It
   * times nothing - its line lasts as long as its Video 1 shots - and shows
   * `overlay.offsetMs` after its line starts. Absent or `main`: an ordinary shot.
   */
  layer?: string | null;
  overlay?: { offsetMs: number } | null;
}

/** Whether a shot is on Video 2 (an overlay) rather than laid end to end on Video 1. */
export const isOverlayShot = (shot: { layer?: string | null }): boolean => shot.layer === "overlay";

/** A line, as much of it as the clock needs. Satisfied by `StoryboardLine`. */
export interface TimelineLineSource {
  key: string;
  position: number;
  /** `line` for an ordinary line; `hook` for a variant of the opening. Anything else is timed as ordinary. */
  kind: string;
  /**
   * Explicit length, used only when the line has no shots. For a source that
   * carries `meta` (a stored line, a `StoryboardDocument` line), only when
   * `meta.durationSet` marks it: unmarked, it is the sum the line's shots made
   * the last time the clock ran, not a length of its own.
   */
  durationMs: number | null;
  /** The stored line's meta, when the source is one; see `durationMs`. */
  meta?: Readonly<Record<string, unknown>> | null;
  shots: readonly TimelineShotSource[];
}

/** What `computeTimeline` reads. Satisfied by `StoryboardDocument`. */
export interface TimelineSource {
  lines: readonly TimelineLineSource[];
}

export interface TimelineLine {
  key: string;
  kind: string;
  /** Milliseconds into the ad. 0 for a hook variant. */
  startMs: number;
  /**
   * The line's length. Null only for a shot-less line with no explicit
   * duration; a line with shots always has a number, an estimate when
   * `partial` (each unknown cut counted as `UNKNOWN_CUT_MS`).
   */
  durationMs: number | null;
  /** True when a shot's cut (or the line's own duration) is unknown, so `durationMs` is an estimate. */
  partial: boolean;
}

export interface TimelineShot {
  key: string;
  /** The line it plays under. */
  line: string;
  /** Milliseconds into the ad: the line's start plus the cuts before it in the line. */
  startMs: number;
  /** As given; null when not known. */
  cutToMs: number | null;
  /** True for a Video 2 shot: `startMs` is where it shows (its line's start plus its offset), and it is not in its line's length. */
  overlay?: boolean;
}

export interface StoryboardTimeline {
  /** The sum of the ordinary lines, in milliseconds. An estimate when `partial`. */
  runtimeMs: number;
  /** True when any line is partial. */
  partial: boolean;
  /** Every line, hooks included, in position order. */
  lines: TimelineLine[];
  /** Every shot of every line, in line order then position order. */
  shots: TimelineShot[];
}

/** Stable position order: ties keep the order they came in, so two equal positions never swap between runs. */
function byPosition<T extends { position: number }>(rows: readonly T[]): T[] {
  return rows
    .map((row, index) => ({ row, index }))
    .sort((a, b) => a.row.position - b.row.position || a.index - b.index)
    .map(({ row }) => row);
}

/** A duration that counts: a finite, non-negative number. Anything else is unknown. */
function known(ms: number | null | undefined): ms is number {
  return typeof ms === "number" && Number.isFinite(ms) && ms >= 0;
}

/**
 * The line's own length, or null when it has none: its `durationMs` as given
 * when the source carries no `meta`, and only when `meta.durationSet` is true
 * when it does.
 */
export function explicitLineDurationMs(line: Pick<TimelineLineSource, "durationMs" | "meta">): number | null {
  const own = line.meta == null || line.meta[LINE_DURATION_SET] === true;
  return own && known(line.durationMs) ? line.durationMs : null;
}

/**
 * What a stored line's `durationMs` holds once the clock has timed it
 * (`timed` is its entry in `computeTimeline`'s answer): its own length when
 * it has one - kept while shots time the line, so it is there again when
 * they go - else the sum of its shots when every cut is known, else null.
 * The sum is written unmarked, which is what keeps it from ever being read
 * back as the line's own.
 */
export function storedLineDurationMs(line: Pick<TimelineLineSource, "durationMs" | "meta">, timed: Pick<TimelineLine, "durationMs" | "partial">): number | null {
  if (line.meta != null && line.meta[LINE_DURATION_SET] === true) return line.durationMs;
  return timed.partial ? null : timed.durationMs;
}

export function computeTimeline(source: TimelineSource): StoryboardTimeline {
  const lines: TimelineLine[] = [];
  const shots: TimelineShot[] = [];
  let runtimeMs = 0;
  let partial = false;

  for (const line of byPosition(source.lines)) {
    const isHook = line.kind === "hook";
    const startMs = isHook ? 0 : runtimeMs;
    const ordered = byPosition(line.shots.filter((s) => !isOverlayShot(s)));

    let durationMs: number | null;
    let linePartial: boolean;
    if (ordered.length === 0) {
      durationMs = explicitLineDurationMs(line);
      linePartial = durationMs === null;
    } else {
      let offset = 0;
      linePartial = false;
      for (const shot of ordered) {
        shots.push({ key: shot.key, line: line.key, startMs: startMs + offset, cutToMs: known(shot.cutToMs) ? shot.cutToMs : null });
        if (known(shot.cutToMs)) offset += shot.cutToMs;
        else {
          offset += UNKNOWN_CUT_MS;
          linePartial = true;
        }
      }
      durationMs = offset;
    }

    lines.push({ key: line.key, kind: line.kind, startMs, durationMs, partial: linePartial });
    if (linePartial) partial = true;
    if (!isHook) runtimeMs += durationMs ?? 0;
  }

  // Overlays (Video 2): each where it shows, its offset after its line's start.
  const startOf = new Map(lines.map((l) => [l.key, l.startMs]));
  for (const line of byPosition(source.lines)) {
    for (const shot of byPosition(line.shots.filter(isOverlayShot))) {
      const at = (startOf.get(line.key) ?? 0) + Math.max(0, shot.overlay?.offsetMs ?? 0);
      shots.push({ key: shot.key, line: line.key, startMs: at, cutToMs: known(shot.cutToMs) ? shot.cutToMs : null, overlay: true });
    }
  }

  return { runtimeMs, partial, lines, shots };
}
